import { Avatar } from '@mui/material';
import { useRouter } from 'next/router';
import {
  Job, JobLocationType, JobPayDisplay, JobPayType, JobType,
} from 'lib/types/jobs';
import { imageAvatarUrl } from 'lib/utils/imageUtils';
import { formatLocalString, formatLocalStringUTC } from 'lib/utils/datetime';
import { jobTypes, jobLocationTypes } from 'lib/constants/jobs';
import { getPayInfo } from 'lib/utils/jobs';
import { useState, useEffect } from 'react';
import { useCurrentUser, useApi } from 'lib/contexts/ApplicationState';
import { userIsAdmin, buildJobApplicantsListPath, handleFindMoreApplicants } from 'lib/helpers';
import { useSnackbar } from 'notistack';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { useModal } from 'mui-modal-provider';
import { useSignUpModal } from 'components/SignUpModal';
import { trackClickExternalApplicationEvent, trackClickInternalApplicationEvent, trackClickSignupEvent } from 'lib/hooks/useEventTracking';
import { ShareButton } from 'components/ShareButton';
import { ProfileLink } from 'components/ProfileLink';
import dynamic from 'next/dynamic';
import { useQuery } from '@tanstack/react-query';
import { ApplyModal } from './ApplyModal';

interface Props {
  job: Job;
}

export const JobDetail = ({ job }: Props) => {
  const router = useRouter();
  const { apply } = router.query;
  const currentUser = useCurrentUser();
  const [open, setIsOpen] = useState(false);
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();
  const { showSignUpModal } = useSignUpModal();
  const ReactMarkdown = dynamic(() => import('react-markdown'), { ssr: false });

  const { data: jobSource } = useQuery({
    enabled: currentUser?.isAdmin,
    queryKey: ['getJobSource', currentUser?.id, job.id],
    queryFn: async () => API.getJobSource(job.id),
  });

  useEffect(() => {
    if (apply && currentUser?.id && !job.appliedAt) {
      setIsOpen(true);
      return;
    }

    if (apply && !currentUser?.id) {
      trackClickSignupEvent(API, 'apply-for-job');
      showSignUpModal('Please sign up or sign in to apply');
    }
  }, [apply, currentUser?.id]);

  if (!job) return null;

  const currentUserIsTeamMember = currentUser
    && currentUser.teams?.some((team) => team.id === job.teamId);

  const clickDelete = () => {
    const modal = showModal(ConfirmationModal, {
      text: 'Are you sure you want to permanently delete this job, and all associated lists/questions?',
      onNo: () => {
        modal.destroy();
      },
      onYes: () => {
        deleteJob();
        modal.destroy();
      },
    });
  };

  const handleClose = async () => {
    try {
      await API.updateJob(job.id, { status: 'closed', teamId: job.teamId });
      enqueueSnackbar('Job closed', {
        variant: 'success',
      });
      router.reload();
    } catch (err) {
      enqueueSnackbar(`Error closing job: ${err}`, {
        variant: 'error',
      });
    }
  };

  const deleteJob = async () => {
    try {
      await API.deleteJob(job.id);
      enqueueSnackbar('Job deleted', {
        variant: 'success',
      });
      router.push('/jobs');
    } catch (error) {
      enqueueSnackbar(`Error loading data: ${error}`, {
        variant: 'error',
      });
    }
  };

  const handleClick = async () => {
    if (!currentUser) {
      trackClickSignupEvent(API, 'apply-for-job');
      showSignUpModal('Please sign up or sign in to apply');
      return;
    }
    if (!currentUser.username) {
      const modal = showModal(ConfirmationModal, {
        title: 'You need to claim a username first!',
        text: 'Claim a username to apply for this job.',
        noLabel: 'Cancel',
        yesLabel: 'Go to settings',
        onNo: () => {
          modal.destroy();
        },
        onYes: () => {
          router.push('/settings');
          modal.destroy();
        },
      });
      return;
    }
    if (!job.externalLink) {
      // track internal apply
      await trackClickInternalApplicationEvent(API, job.id);
    }
    setIsOpen(true);
  };

  const trackExternalApplyLink = async () => {
    await trackClickExternalApplicationEvent(API, job.id);
  };
  const getApplyButton = () => {
    if (job.appliedAt) {
      return (
        <span className="bg-lightgray px-4 py-2 text-sm font-semibold rounded-md">
          Applied on
          {' '}
          {formatLocalString(job.appliedAt)}
        </span>
      );
    }
    return (
      <button type="button" className="btn-primary" onClick={() => handleClick()} disabled={job.status !== 'open'}>Apply</button>
    );
  };

  const onCloseApplicationModal = () => {
    setIsOpen(false);
    // clear the apply query from router
    router.push(`/jobs/${job.id}`);
  };

  return (
    <div className="max-w-screen-lg mx-auto py-8 px-4">
      <div className="flex flex-wrap sm:flex-nowrap gap-6 items-center mb-4">
        <div className="w-full sm:w-1/5">
          <ProfileLink
            user={job.account}
            username={job.account.username}
            linkToExplore={job.account.isHidden}
            context="job-detail"
          >
            <Avatar
              sx={{ width: 100, height: 100, margin: 'auto' }}
              variant="circular"
              alt={job.account.name}
              src={imageAvatarUrl(job.account.avatarUrl, 100)}
            />
          </ProfileLink>
        </div>
        <div className="w-full sm:w-4/5 text-center sm:text-left">
          <h1 className="text-2xl font-bold mb-4">
            {job.name}
          </h1>
          <div className="flex justify-center sm:justify-start">
            <h2 className="text-xl font-semibold mb-4">
              <ProfileLink
                user={job.account}
                username={job.account.username}
                linkToExplore={job.account.isHidden}
              >
                {job.account.name}
              </ProfileLink>
            </h2>
            <div className="px-4">&bull;</div>
            <h3 className="text-lg">
              @
              {job.account.username}
            </h3>
          </div>
          <div className="flex mb-4 text-lg justify-center sm:justify-start">
            <div suppressHydrationWarning className={`${job.status === 'closed' && 'text-red font-bold'}`}>
              {(job.createdAt && job.status === 'open') ? `Posted on ${formatLocalString(job.createdAt)}` : job.status?.toLocaleUpperCase()}
            </div>
            <div className="px-4">&bull;</div>
            <div>{jobTypes[job.jobType as JobType]}</div>
          </div>
          {userIsAdmin(currentUser) && jobSource && (
            <div>
              <strong>Source:</strong>
              {' '}
              {jobSource.source}
              {' '}
              (created by
              {' '}
              {jobSource.createdBy}
              )
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center justify-center sm:justify-start">
            <ApplyModal
              job={job}
              open={open}
              onCancel={onCloseApplicationModal}
              handleTrackClick={trackExternalApplyLink}
            />
            {getApplyButton()}
            {(currentUserIsTeamMember || userIsAdmin(currentUser)) && (
              <>
                {job.list && (
                  <button type="button" className="btn-primary-outlined" onClick={() => router.push(buildJobApplicantsListPath(job.list.name, job.list.publicId))}>
                    View applicants
                  </button>
                )}
                <button type="button" className="btn-primary-outlined" onClick={() => router.push(handleFindMoreApplicants(job.roles || [], job.locations || []))}>
                  Find qualified creators
                </button>
                <button type="button" className="btn-primary-outlined" onClick={() => router.push(`/jobs/${job.id}/edit`)}>Edit</button>
                <button type="button" className="btn-secondary" onClick={handleClose} disabled={job.status === 'closed'}>Close</button>
              </>
            )}
            <ShareButton text={job.name} size="medium" />
          </div>
        </div>
      </div>
      <div className="border p-8 rounded-lg mb-4">
        <div className="flex flex-wrap-reverse md:flex-nowrap md:gap-4">
          <div className="w-full md:w-2/3 mb-4">
            <h2 className="text-xl font-semibold mb-4">Job Description</h2>
            <div key={job.id} className="mb-4 html-content">
              <ReactMarkdown>{job.description}</ReactMarkdown>
            </div>
          </div>
          <div className="w-full md:w-1/3">
            <div>Location</div>
            <div className="text-lg font-semibold">
              {jobLocationTypes[job.locationType as JobLocationType]}
            </div>
            <div className="text-lg font-semibold mb-4">
              {job.locations?.map((l) => (
                <div key={l.id}>{l.name}</div>
              ))}
            </div>
            <div>Pay</div>
            <div className="text-lg font-semibold mb-4">
              {getPayInfo(
                job.payType as JobPayType,
                job.payDisplay as JobPayDisplay,
                job.payAmount, job.payMin, job.payMax,
              )}
            </div>
            {
              job.roles && job.roles.length > 0 && (
                <>
                  <div>Similar roles/responsibilities</div>
                  <div className="text-lg font-semibold mb-4">
                    {job.roles?.map((r) => (
                      <div key={r.id}>{r.name}</div>
                    ))}
                  </div>
                </>
              )
            }
            {
              job.startDate && (
                <>
                  <div>Start Date</div>
                  <div className="text-lg font-semibold mb-4">
                    {new Date(job.startDate) < new Date() ? 'Immediate'
                      : formatLocalStringUTC(job.startDate)}
                  </div>
                </>
              )
            }
            {
              job.endDate && (
                <>
                  <div>End Date</div>
                  <div className="text-lg font-semibold mb-4">
                    {formatLocalStringUTC(job.endDate)}
                  </div>
                </>
              )
            }
          </div>
        </div>
      </div>
      {!currentUserIsTeamMember && <div className="text-center sm:hidden">{getApplyButton()}</div>}
      {userIsAdmin(currentUser) && (
        <button type="button" className="btn-secondary-outlined" onClick={clickDelete}>Delete (admin)</button>
      )}
    </div>
  );
};
